import { z } from 'zod'
import { captureSourceEnum, docId, isoTimestamp, money } from './common.js'

/**
 * Where a receipt is in the extraction pipeline (§7.1).
 *
 * `pending` is written by the uploader, the rest by the extraction function.
 * A receipt captured before extraction existed has no `extraction` block at
 * all, which is why the field below defaults rather than being required.
 */
export const extractionStatusEnum = z.enum(['pending', 'done', 'failed'])
export type ExtractionStatus = z.infer<typeof extractionStatusEnum>

/** Mirrors `domain/receiptExtraction`'s rejection reasons. */
export const extractionRejectionSchema = z.object({
  field: z.enum(['vendor', 'amount', 'date', 'overall']),
  reason: z.enum([
    'email_boilerplate',
    'payment_confirmation_text',
    'cardholder_name',
    'unknown_placeholder',
    'implausible_vendor',
    'low_confidence',
  ]),
  rejected: z.string().max(500),
})

/**
 * The extraction attempt itself, as written by the `extractReceipt` function.
 *
 * Kept beside the extracted values rather than merged into them because the
 * review screen has to explain *why* a field is empty. §6.1's whole point is
 * that a refused reading announces itself — a null vendor with no reason
 * attached is indistinguishable from a receipt nobody has processed yet.
 *
 * Every field is optional: the uploader writes only `status`, the success path
 * and the failure path write disjoint subsets, and older receipts have none of
 * it. Anything absent simply isn't known.
 */
export const receiptExtractionSchema = z.object({
  status: extractionStatusEnum,
  /** True when anything was rejected, confidence was low, or the read failed. */
  needsReview: z.boolean().default(false),
  rejections: z.array(extractionRejectionSchema).default([]),
  /** Model's own remark about the capture — glare, cropping, missing total. */
  notes: z.string().max(2000).nullable().default(null),
  /** Set when a date was read but fell outside a plausible window. */
  dateRejected: z.boolean().default(false),
  /** True when the image was re-encoded (HEIC, or downscaled) before reading. */
  converted: z.boolean().default(false),
  /** Present only on `failed`: a sentence to show the user. */
  failure: z.string().max(500).nullable().default(null),
  /** `failed` only. A permanent failure is not worth retrying. */
  permanent: z.boolean().default(false),
  durationMs: z.number().int().nonnegative().nullable().default(null),
  inputTokens: z.number().int().nonnegative().nullable().default(null),
  outputTokens: z.number().int().nonnegative().nullable().default(null),
})

export type ReceiptExtraction = z.infer<typeof receiptExtractionSchema>

/**
 * `receipts` — REQUIREMENTS.md §5.4. Unused until Phase 2.
 *
 * One document per receipt file. A receipt starts life unattached — captured
 * before we know which transaction it belongs to — and is linked during review.
 * The raw file lands in Storage before any processing, so a failed extraction
 * never loses the capture (§9, reliability).
 */
export const receiptSchema = z.object({
  id: docId.optional(),
  userId: z.string().min(1),

  captureSource: captureSourceEnum,
  originalFileUrl: z.string().min(1),
  renderedPdfUrl: z.string().nullable().default(null),
  pageImageUrls: z.array(z.string()).default([]),

  emailFromAddress: z.email().nullable().default(null),
  emailSubject: z.string().max(500).nullable().default(null),
  /** Storage path of the message exactly as received (§6.1). */
  rawEmailUrl: z.string().nullable().default(null),
  /**
   * False when the sender wasn't on the trusted list — §6.1 wants those
   * confirmed before they're acted on. The receipt is still created: mail that
   * clears the routing token is kept and flagged, never silently discarded.
   */
  emailSenderTrusted: z.boolean().default(true),
  capturedAt: isoTimestamp,

  extractedVendor: z.string().trim().max(200).nullable().default(null),
  extractedAmount: money.nullable().default(null),
  extractedDate: isoTimestamp.nullable().default(null),
  extractionConfidence: z.number().min(0).max(1).nullable().default(null),
  /** Model name + version, for auditability. */
  extractionModel: z.string().max(120).nullable().default(null),
  extraction: receiptExtractionSchema.nullable().default(null),

  /** Primary transaction when split; siblings found via `splitGroupId`. */
  linkedTransactionId: docId.nullable().default(null),
  splitGroupId: docId.nullable().default(null),
  linkedAt: isoTimestamp.nullable().default(null),

  /** True if the user edited any extracted field. */
  userOverride: z.boolean().default(false),

  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
})

export type Receipt = z.infer<typeof receiptSchema>

/**
 * Confidence at or above this is treated as high (§7.1 step 6): a single Save
 * button rather than a pre-filled review form.
 */
export const HIGH_CONFIDENCE_THRESHOLD = 0.85
